/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useEffect, useState} from 'react';
import {Edit} from '@mui/icons-material';
import {NavLink, useSearchParams} from 'react-router-dom';
import {Helmet} from 'react-helmet-async';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {Grid, TableCell, TableBody, IconButton} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {FilterList as FilterListIcon} from '@mui/icons-material';
// eslint-disable-next-line node/no-extraneous-import
import momentTimezone from 'moment-timezone';

import {ColumnsType, typeRoutes} from '../../types/typeIslandersMembers';
import {path} from '../../routes/path';
import {Breadcrumbs, Button, Chip, Table, Tooltips} from '../../components';
import ModalAddNew from './components/ModalAddNew/ModalAddNew';
import IslanderService from '../../services/Islander/islander.service';
import useAuth from '../../hooks/useAuth';
import {
  createFilterSelectOptions,
  envRole,
  formatDateTime,
  formatLocalDateFromUTCTime,
  hasPermission,
} from '../../utils';
import ModalFilter from './components/ModalFilter/ModalFilter';
import {ColorType} from '../../types/typeChip';
import queryString from 'query-string';

// style
import {
  Typography,
  Divider,
  StyledTableRow,
  StyledAction,
  StyledIcon,
} from './IslandPartner.style';
import {USER_TYPE} from '../../constants/UserType';
import ExportExcelService from '../../services/ExportExcel/ExportExcel';
import success from '../../constants/success';
import {rbacPermissions} from '../../constants/roleBasedAccessControlConstants';
import usePageFilter from '../../hooks/usePageFilter';

interface DateTimeRange {
  startOfDateTime?: Date | null;
  endOfDateTime?: Date | null;
}

const islandPartnerTitle = `Island Partner${
  +envRole === USER_TYPE.ADMINISTRATOR ? 's' : ''
}`;

function IslandPartner() {
  const dateTimeFormat = 'DD/MM/YYYY HH:mm:ss';
  const rangePickerDateTimeFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
  const [searchParams, setSearchParams] = useSearchParams();
  const {getUser, showNotification, checkRbac} = useAuth();
  const {merchantId, email} = getUser() || {};
  const role =
    +envRole === USER_TYPE.ADMINISTRATOR ? 'administrator' : 'merchant';
  const canCreate = hasPermission(path.ISLAND_PARTNERS, 'create');
  const canUpdate = hasPermission(path.ISLAND_PARTNERS, 'update');

  const navigate = useNavigate();

  // state
  const [order] = useState<'desc' | 'asc'>('asc');
  const [orderBy] = useState('');
  const [isModalFilter, setIsModalFilter] = useState(false);
  const [dense] = useState(false);
  const [isModalAddNew, setIsModalAddNew] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [dataSource, setDataSource] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPage, setTotalPage] = useState(0);
  const [error, setError] = useState();
  const filterQrCodeExpiryStatusOptions = [
    createFilterSelectOptions('Active'),
    createFilterSelectOptions('Expired'),
  ];
  const filterListedInAppParticipatingOutlets = [
    createFilterSelectOptions('Yes', 'true'),
    createFilterSelectOptions('No', 'false'),
  ];
  const defaultFilterCreatedDateTimeRange: DateTimeRange = {
    startOfDateTime: null,
    endOfDateTime: null,
  };
  const [valueCreatedDateTimeRangePicker, setValueCreatedDateTimeRangePicker] =
    useState<DateTimeRange>(defaultFilterCreatedDateTimeRange);
  const defaultFilterQrCodeExpiryDateTimeRange: DateTimeRange = {
    startOfDateTime: null,
    endOfDateTime: null,
  };
  const [
    valueQrCodeExpiryDateTimeRangePicker,
    setValueQrCodeExpiryDateTimeRangePicker,
  ] = useState<DateTimeRange>(defaultFilterQrCodeExpiryDateTimeRange);
  const customRenderFilterChipLabel = {
    showOnMobileParticipatingList_eq: (value: any) =>
      ['true', 'false'].includes(`${value}`) &&
      `${
        `${value}` === 'true' ? 'Listed' : 'Not listed'
      } in App's participating outlets`,
  };
  const filterDateRangeConfig: any = [
    {
      filterKeys: ['createdDateTime_gte', 'createdDateTime_lte'],
      dateTimeRangPickerState: valueCreatedDateTimeRangePicker,
      renderChipFormat: dateTimeFormat,
      callbackOnDeleteChip: () =>
        setValueCreatedDateTimeRangePicker(defaultFilterCreatedDateTimeRange),
      handleDeleteChipKey: 'createdDateTime_dateRangePicker',
    },
    {
      filterKeys: ['qrExpiredDate_gte', 'qrExpiredDate_lte'],
      dateTimeRangPickerState: valueQrCodeExpiryDateTimeRangePicker,
      renderChipFormat: dateTimeFormat,
      callbackOnDeleteChip: () =>
        setValueQrCodeExpiryDateTimeRangePicker(
          defaultFilterQrCodeExpiryDateTimeRange
        ),
      handleDeleteChipKey: 'qrExpiredDate_dateRangePicker',
    },
  ];
  const defaultValueFilter: any = {
    id_eq: searchParams.get('id_eq') || '',
    name_contains: searchParams.get('name_contains') || '',
    createdDateTime_gte: searchParams.get('createdDateTime_gte') || '',
    createdDateTime_lte: searchParams.get('createdDateTime_lte') || '',
    qrExpiredDate_gte: searchParams.get('qrExpiredDate_gte') || '',
    qrExpiredDate_lte: searchParams.get('qrExpiredDate_lte') || '',
    qrCodeExpiryStatus_eq: searchParams.get('qrCodeExpiryStatus_eq') || '',
    showOnMobileParticipatingList_eq:
      searchParams.get('showOnMobileParticipatingList_eq') || '',
  };
  const defaultParams = {
    _page: 0,
    _size: 10,
    status_eq: 1,
  };

  const callbackAfterHandleFilter = () => {
    toggleModalFilter();
  };

  const overwriteGetParamValue = {
    qrCodeExpiryStatus_eq: (value: any) => {
      if (['active', 'expired'].includes(`${value}`.toLocaleLowerCase())) {
        if (value.toLocaleLowerCase() === 'active') {
          return {
            qrExpiredDate_gte: formatDateTime(
              new Date(),
              rangePickerDateTimeFormat
            ),
            qrExpiredDate_lte: undefined,
          };
        } else if (value.toLocaleLowerCase() === 'expired') {
          return {
            qrExpiredDate_gte: undefined,
            qrExpiredDate_lte: formatDateTime(
              new Date(),
              rangePickerDateTimeFormat
            ),
          };
        }
      }
    },
  };

  const {
    valueFilter,
    setValueFilter,
    params,
    setParams,
    handleFilter,
    handleClean,
    renderFilterChips,
    getFilterParams,
  } = usePageFilter({
    defaultValueFilter,
    defaultParams,
    filterDateRangeConfig,
    rangePickerDateTimeFormat,
    callbackAfterHandleFilter,
    customRenderFilterChipLabel,
    overwriteGetParamValue,
  });

  const [isLoadingExport, setIsLoadingExport] = useState(false);

  const columns: Array<ColumnsType> = [
    {
      dataIndex: 'id',
      numeric: false,
      disablePadding: false,
      label: 'ID',
    },
    {
      dataIndex: 'merchant_name',
      numeric: false,
      disablePadding: false,
      label: 'Island Partner Name',
    },
    {
      dataIndex: 'createdDateTime',
      numeric: false,
      disablePadding: false,
      label: 'Created date time',
    },
    {
      dataIndex: 'qrExpiredDate',
      numeric: false,
      disablePadding: false,
      label: 'QR code expiry date',
    },
    {
      dataIndex: 'qr_code_status',
      numeric: false,
      disablePadding: false,
      label: 'QR code status',
    },
    {
      dataIndex: 'showOnMobileParticipatingList',
      numeric: false,
      disablePadding: false,
      label: "Listed in App's participating outlets",
    },
    {
      dataIndex: null,
      numeric: false,
      disablePadding: false,
      label: '',
    },
  ];

  const onChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams(preState => ({
      ...preState,
      _page: newPage,
    }));
  };

  const onChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setParams(preState => ({
      ...preState,
      _page: 0,
      _size: parseInt(event.target.value, 10),
    }));
  };

  const renderAction = (value: string) => {
    if (
      !checkRbac({
        permissionString: [
          rbacPermissions.ISLAND_PARTNERS__LISTING__VIEW,
          rbacPermissions.ISLAND_PARTNERS__LISTING__EDIT,
        ],
      })
    ) {
      return null;
    }
    if (!canUpdate) {
      if (focusId === value) {
        return (
          <StyledAction>
            <Tooltips title="View detail">
              <IconButton
                aria-label="details"
                size="medium"
                onClick={() => {
                  navigate(`/${path.ISLAND_PARTNERS}/${value}`, {
                    state: {
                      ...queryString.parse(searchParams.toString()),
                    },
                  });
                }}
              >
                <VisibilityIcon style={StyledIcon} />
              </IconButton>
            </Tooltips>
          </StyledAction>
        );
      }
    }
    if (focusId === value) {
      return (
        <StyledAction>
          <Tooltips title="Edit">
            <IconButton
              aria-label="details"
              size="medium"
              onClick={() => {
                navigate(`/${path.ISLAND_PARTNERS}/${value}`, {
                  state: params,
                });
              }}
            >
              <Edit style={StyledIcon} />
            </IconButton>
          </Tooltips>
        </StyledAction>
      );
    }
  };

  const toggleModal = () => {
    setIsModalAddNew(!isModalAddNew);
  };

  const getIslanderPartners = () => {
    setIsLoading(true);
    new IslanderService()
      .getAll({
        ...getFilterParams(),
        // Overwrite id if user is on Island Partner Portal
        ...(role === 'merchant'
          ? {
              id_eq: merchantId,
            }
          : {}),
      })
      .then(res => {
        if (res?.data && Array.isArray(res.data.items)) {
          setDataSource(res.data.items);
          setTotalPage(res?.data?.total);
          setIsLoading(false);
        }
      })
      .catch(error => {
        setIsLoading(false);
        // Get api error => show notification or no items listing
        setError(error);
      });
  };

  const toggleModalFilter = () => {
    setIsModalFilter(!isModalFilter);
  };

  const getExport = () => {
    setIsLoadingExport(true);
    new ExportExcelService()
      .exportIslandPartner({email: email, name_contains: params.name_contains})
      .then(res => {
        setIsLoadingExport(false);
        if (res?.success) {
          showNotification({
            message: success.EXPORT_SUCCESS,
          });
        } else {
          showNotification({
            message: res?.errorMessage,
            variation: 'error',
          });
        }
      });
  };

  // useEffect(() => {
  //   if (typeof window !== 'undefined') {
  //     window.gtag('js', new Date());
  //     window.gtag('config', 'G-EQ3JMF90YJ');
  //   }
  // }, []);

  useEffect(() => {
    if (role === 'merchant') {
      navigate(`/${path.ISLAND_PARTNERS}/${merchantId}`);
    }
  }, [role]);

  useEffect(() => {
    if (role && role !== 'merchant') {
      setSearchParams(queryString.stringify(params));
      getIslanderPartners();
    }
  }, [params, role]);

  return (
    <React.Fragment>
      <Helmet title={islandPartnerTitle} />
      <ModalFilter
        visible={isModalFilter}
        onClose={toggleModalFilter}
        handleFilter={handleFilter}
        handleClean={handleClean}
        valueFilter={valueFilter}
        setValueFilter={setValueFilter}
        valueCreatedDateTimeRangePicker={valueCreatedDateTimeRangePicker}
        setValueCreatedDateTimeRangePicker={setValueCreatedDateTimeRangePicker}
        valueQrCodeExpiryDateTimeRangePicker={
          valueQrCodeExpiryDateTimeRangePicker
        }
        setValueQrCodeExpiryDateTimeRangePicker={
          setValueQrCodeExpiryDateTimeRangePicker
        }
        filterQrCodeExpiryStatusOptions={filterQrCodeExpiryStatusOptions}
        filterListedInAppParticipatingOutlets={
          filterListedInAppParticipatingOutlets
        }
      />
      <ModalAddNew
        visible={isModalAddNew}
        onClose={toggleModal}
        reloadPage={getIslanderPartners}
      />
      <Grid justifyContent="space-between" container spacing={2} columns={16}>
        <Grid item xs={8}>
          <Typography variant="h3" gutterBottom>
            Island Partners
          </Typography>

          <Breadcrumbs routes={[typeRoutes(islandPartnerTitle, null, true)]} />
        </Grid>

        <Grid
          item
          xs={8}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          {renderFilterChips()}
          <div
            style={{
              marginRight: '10px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {checkRbac(
              {
                permissionString:
                  rbacPermissions.ISLAND_PARTNERS__LISTING__EXPORT,
              },
              <Button
                onClick={getExport}
                width="100px"
                variant="outlined"
                loading={isLoadingExport}
              >
                Export
              </Button>
            )}
          </div>
          {!!canCreate &&
            checkRbac({
              permissionString:
                rbacPermissions.ISLAND_PARTNERS__LISTING__CREATE,
            }) && (
              <Button onClick={toggleModal} loading={isLoading}>
                Add new
              </Button>
            )}

          <Tooltips title="Filter list">
            <IconButton
              aria-label="Filter list"
              size="large"
              onClick={toggleModalFilter}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltips>
        </Grid>
      </Grid>

      <Divider my={6} />
      <Table
        dataSource={dataSource}
        columns={columns}
        page={params._page}
        isLoading={isLoading}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        // onRequestSort={onRequestSort}
        rowsPerPage={params._size}
        textNodata="There are no island partner(s) matching the filter."
        order={order}
        orderBy={orderBy}
        dense={dense}
        isMultiCheckbox={false}
        count={totalPage}
      >
        {!error && (
          <TableBody>
            {dataSource?.map((row: any) => {
              const isQrCodeExpired =
                new Date(
                  momentTimezone
                    .tz(row.qrExpiredDate, 'Asia/Singapore')
                    .toDate()
                ) < new Date();
              return (
                <StyledTableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row.id}
                  onMouseEnter={() => setFocusId(row.id)}
                  onMouseLeave={() => setFocusId(null)}
                >
                  <TableCell component="th" scope="row">
                    {row.id}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell>
                    {formatLocalDateFromUTCTime(
                      row.createdDateTime,
                      dateTimeFormat
                    )}
                  </TableCell>
                  <TableCell>
                    {formatLocalDateFromUTCTime(
                      row.qrExpiredDate,
                      dateTimeFormat
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isQrCodeExpired ? 'Expired' : 'Active'}
                      color={
                        (isQrCodeExpired ? 'error' : 'success') as ColorType
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell width={150}>
                    {row.showOnMobileParticipatingList === true ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell
                    align="right"
                    padding="checkbox"
                    style={{paddingRight: '10px'}}
                  >
                    {renderAction(row.id)}
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
    </React.Fragment>
  );
}

export default IslandPartner;
